// frontend/src/admin/pages/AdminVoucherDetailPage.jsx
import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

import { getVoucher } from "../api";

function fmtDate(s) {
  return s ? new Date(s).toLocaleString() : "—";
}

/** AdminVoucherDetailPage — voucher fields + full redemption history table. */
function AdminVoucherDetailPage() {
  const { voucherId } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setV(await getVoucher(voucherId));
      setError(null);
    } catch {
      setError("Failed to load voucher.");
    } finally {
      setLoading(false);
    }
  }, [voucherId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <p className="text-sm text-slate-500">Loading…</p>;
  if (error || !v)
    return <p className="text-sm text-red-600">{error ?? "Not found."}</p>;

  return (
    <div className="max-w-3xl">
      <Link
        to="/admin/vouchers"
        className="text-sm text-indigo-600 hover:text-indigo-500"
      >
        ← All vouchers
      </Link>

      <h1 className="text-xl font-semibold text-slate-900 font-mono mt-2 mb-4">
        {v.code}
      </h1>

      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-4">
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-500">Type</dt>
          <dd className="text-slate-900">{v.discount_type}</dd>
          <dt className="text-slate-500">Value</dt>
          <dd className="text-slate-900">{String(v.discount_value)}</dd>
          <dt className="text-slate-500">Redemptions</dt>
          <dd className="text-slate-900">
            {v.redeemed_count}
            {v.max_redemptions ? ` / ${v.max_redemptions}` : " (unlimited)"}
          </dd>
          <dt className="text-slate-500">Status</dt>
          <dd className="text-slate-900">
            {v.is_active ? "Active" : "Inactive"}
          </dd>
          <dt className="text-slate-500">Stripe coupon</dt>
          <dd className="text-slate-900">{v.stripe_coupon_id ?? "—"}</dd>
        </dl>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">
          Redemption history ({v.redemptions?.length ?? 0})
        </h2>
        {(v.redemptions?.length ?? 0) === 0 ? (
          <p className="text-sm text-slate-500">No redemptions yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left py-1.5">Organisation</th>
                <th className="text-left py-1.5">Redeemed</th>
                <th className="text-right py-1.5">Discount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {v.redemptions.map((r) => (
                <tr key={`${r.org_id}-${r.redeemed_at}`}>
                  <td className="py-1.5 text-slate-900">
                    {r.org_name ?? r.org_id}
                  </td>
                  <td className="py-1.5 text-slate-500">
                    {fmtDate(r.redeemed_at)}
                  </td>
                  <td className="py-1.5 text-right text-slate-900">
                    {String(r.discount_applied)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default AdminVoucherDetailPage;
